import { format } from 'date-fns'
import { Avatar, Box, Card, Flex, Heading, Text } from 'ui'

import { toTitleCase } from 'lib/legacy_util'
import { CalendarIcon, MapPin } from 'lucide-react'
import { PropsWithChildren, useMemo } from 'react'
import { useTheme } from 'styled-components'
import { useWorkerProfileContext } from '../context/WorkerProfileProvider'
import { Certifications } from './Certifications'
import { Compliments } from './Compliments'
import { ShiftStats } from './ShiftStats'
import { WorkerStats } from './WorkerStats'
import { WorkHistory } from './WorkHistory'
import { WorkerActions } from 'components/WorkerActions'

type Props = PropsWithChildren<{
  Icon: React.ComponentType<{ size: number }>
}>

const InfoItem = ({ Icon, children }: Props) => {
  const theme = useTheme()

  return (
    <Flex
      alignItems="center"
      gap={2}
      lineHeight={2}
      color={theme.colors.lightText}
    >
      <Icon size={16} />
      <Text>{children}</Text>
    </Flex>
  )
}

export function WorkerDetails() {
  const { state: profile } = useWorkerProfileContext()

  const address = profile?.worker?.address
    ? [profile.worker.address.city, profile.worker.address.state]
        .filter(Boolean)
        .join(', ')
    : null

  const firstActive = profile?.worker?.firstActive
    ? format(new Date(profile.worker.firstActive), 'MMMM yyyy')
    : null

  const worker = useMemo(() => {
    if (profile?.worker) {
      return {
        id: profile.worker.id,
        name: profile.worker.name || '',
        firstActive: profile.worker.firstActive
          ? format(new Date(profile.worker.firstActive), 'MMMM yyyy')
          : '',
        rating: profile.worker.rating ?? 0,
        numShiftsForCompany: profile.worker.numShiftsForCompany ?? 0,
        numFinishedShifts: profile.worker.numFinishedShifts ?? 0,
        statusForCompany: profile.worker.statusForCompany ?? null,
        positionTemplateBlocks: profile.worker.positionTemplateBlocks ?? [],
        rosters:
          profile.worker.rosters?.map((roster) => ({
            id: parseInt(roster.id),
            name: roster.name ?? '',
          })) ?? [],
        favorites: profile.worker.favorites ?? 0,
        profilePicUrl: profile.worker.profilePicUrl,
        address: profile.worker.address,
      }
    }
  }, [profile?.worker])

  if (!worker) {
    throw new Error('Worker not found')
  }

  return (
    <Card
      borderRadius={'xlarge'}
      shadowless={true}
      borderWidth={0}
      mt={[4, 4, 6]}
      px={[3, 3, 6]}
    >
      <Flex flexDirection={'column'} alignItems={'center'}>
        <Box mt={'-7rem'}>
          <Avatar
            user={{
              name: worker.name,
              photoUrl: worker.profilePicUrl,
            }}
            size={'xxlarge'}
          />
        </Box>
        <Heading level={1}>{toTitleCase(profile.worker.name)}</Heading>
        <Flex flexDirection={'column'} alignItems={'center'} mb={3}>
          {address ? <InfoItem Icon={MapPin}>{address}</InfoItem> : null}
          {firstActive ? (
            <InfoItem Icon={CalendarIcon}>Active Since {firstActive}</InfoItem>
          ) : null}
        </Flex>

        <WorkerActions worker={worker} />
      </Flex>

      <WorkerStats />
      <Compliments />
      <Certifications />
      <ShiftStats />
      <WorkHistory />
    </Card>
  )
}
